<template>
  <div>
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      :z-index="menuZindex"
      @update:model-value="closeModal"
    >
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          :ripple="false"
          :secondary="!menu"
          :primary="menu"
          :min-width="170"
          variant="flat"
          :height="28"
          data-dd-action-name="OpenAssortmentCanvasProductFilterBtn"
        >
          {{ productsAndFiltersShown }}
        </v-btn>
      </template>
      <v-card
        min-width="500"
        max-width="500"
        max-height="500"
        style="overflow-y: auto"
        class="mt-2"
      >
        <div class="pa-4 d-flex flex-column">
          <div class="d-flex flex-column">
            <div class="mb-2">
              <v-select
                v-model="filterValueModel"
                class="rtls-select rtls-select--white"
                persistent-placeholder
                :items="filterItemsSorted"
                item-title="name"
                item-value="type"
                :placeholder="$t('general.select')"
                hide-details
                return-object
                @update:model-value="onFilterFieldChange"
              />
            </div>
            <div>
              <v-select
                v-model="selectedOperator"
                :placeholder="$t('general.select')"
                persistent-placeholder
                :disabled="isOperatorInputDisabled"
                :items="operatorOptions"
                item-title="name"
                item-value="value"
                hide-details
                density="compact"
                class="rtls-select rtls-select--white"
                @update:model-value="onOperatorChange"
              />
            </div>
            <div class="mb-4 d-flex align-center">
              <v-autocomplete
                ref="autocompleteSelect"
                v-model="selectedFilterValues"
                v-model:search="searchInput"
                :items="selectedFilterItems"
                chips
                item-title="name"
                item-value="id"
                :multiple="allowSelectMultiple"
                :disabled="isFilterValuesInputDisabled"
                bg-color="white"
                density="compact"
                hide-details
                hide-selected
                return-object
                :menu-props="{ closeOnClick: true }"
                @update:model-value="searchInput = ''"
                @click:append="menuArrow"
              >
                <template #chip="{ item }">
                  <v-chip
                    :text="item.title"
                    size="small"
                    closable
                    @click:close="deleteChip(item)"
                  />
                </template>
              </v-autocomplete>
              <v-tooltip location="top">
                <template #activator="{ props: activatorProps }">
                  <v-btn
                    v-bind="activatorProps"
                    icon
                    variant="text"
                    :disabled="isAddFilterDisabled"
                    class="ml-3 add-filter-button"
                    @click="addFilter"
                  >
                    <v-icon color="primary"> mdi-plus-box </v-icon>
                  </v-btn>
                </template>
                <span>{{
                  isAddFilterDisabled ? tkey('selectAValueToAddFilter') : tkey('addFilter')
                }}</span>
              </v-tooltip>
            </div>
            <div class="mb-5 d-flex flex-column">
              <div v-if="filters.length" class="d-flex flex-column">
                <h2 class="title-filters">
                  {{ labelAppliedFilters }}
                </h2>
                <div class="d-flex">
                  <v-col class="headers pr-0 pb-1">
                    <h4 class="pb-0">
                      {{ labelFilterName }}
                    </h4>
                  </v-col>
                  <v-col class="headers pr-0 pb-1">
                    <h4 class="pb-0">
                      {{ tkey('operator') }}
                    </h4>
                  </v-col>
                  <v-col class="headers pr-0 pb-1">
                    <h4 class="pb-0">
                      {{ labelFilterValues }}
                    </h4>
                  </v-col>
                  <v-col :cols="1" class="headers pb-1 pr-1" />
                </div>
                <div
                  v-for="(filter, index) in filters"
                  :key="filter.filterType + index"
                  class="d-flex filter-row"
                >
                  <v-col class="filter-item pr-0 pt-1 pb-1 d-flex flex-column justify-center">
                    {{ filter.filterName }}
                  </v-col>
                  <v-col class="filter-item pr-0 pt-1 pb-1 d-flex flex-column justify-center">
                    {{ tkey(filter.operator) }}
                  </v-col>
                  <v-col class="filter-item pr-0 pt-1 pb-1 d-flex flex-column justify-center">
                    <div
                      v-for="(value, valueIndex) in filter.filterValues"
                      :key="value.id + valueIndex"
                    >
                      {{ value.name }}
                    </div>
                  </v-col>
                  <v-col
                    :cols="1"
                    class="filter-item pt-1 pb-1 pr-1 d-flex flex-column justify-center align-center"
                  >
                    <v-btn icon variant="text" class="ml-1" @click="removeFilter(index)">
                      <v-icon size="small" color="primary"> mdi-close-circle </v-icon>
                    </v-btn>
                  </v-col>
                </div>
              </div>
              <div v-else>
                <h3>{{ labelNoFiltersApplied }}</h3>
              </div>
            </div>
          </div>
          <div class="d-flex justify-end align-center close-icon-container">
            <v-btn
              class="mr-0 font-weight-bold btn-text"
              :ripple="false"
              variant="text"
              size="small"
              @click="[(menu = false), closeModal(false)]"
            >
              {{ $t('actions.close') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { useDayjs } from '@/composables/use-dayjs';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';
import customerSegmentationMixin from '@/mixins/customer-segmentation';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import { useClusteringStore } from '@/stores/clustering';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import { useToolDataStore } from '@/stores/tool-data';
import assortmentCanvases from '@enums/assortment-canvases';
import menus from '@enums/menus';
import SpacebreakTypes from '@enums/spacebreak-types';
import {
  cloneDeep,
  find,
  get,
  isArray,
  isEmpty,
  isPlainObject,
  map,
  size,
  sortBy,
  upperFirst,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

const FILTER = 'filter';

const { Filters, FilterOperators, FilterValues, FilterMappings } = assortmentCanvases;

export default {
  mixins: [customerSegmentationMixin],
  props: {
    mode: {
      type: String,
      required: true,
      default: 'filter', // filter or highlight
    },
  },
  setup() {
    const { tkey } = useInternationalization('filters');
    const { formatDate, sortDates } = useDayjs();
    const { historicKPIsEnabled } = useFeature();
    return { tkey, formatDate, sortDates, historicKPIsEnabled };
  },
  data() {
    return {
      selectedOperator: null,
      searchInput: '',
      menu: false,
      attributesValuesMap: {},
      customAttributeItemsWithType: [],
      filterValue: { name: null, type: null },
      baseFilterItems: [
        {
          name: this.tkey(Filters.ProductType),
          type: FilterMappings.ProductType,
        },
        {
          name: this.tkey(Filters.LockType),
          type: Filters.LockType,
        },
        {
          name: this.tkey(Filters.CurrentSpacebreak),
          type: FilterMappings.CurrentSpacebreak,
        },
        {
          name: this.tkey(Filters.OptimisedSpacebreak),
          type: FilterMappings.OptimisedSpacebreak,
        },
        {
          name: this.tkey(Filters.OriginalSpacebreak),
          type: FilterMappings.OriginalSpacebreak,
        },
        {
          name: this.tkey(Filters.PromotedDemoted),
          type: Filters.PromotedDemoted,
        },
        {
          name: this.tkey(Filters.ProductSeries),
          type: Filters.ProductSeries,
        },
      ],
      filterItems: [],
      selectedFilterValues: [],
      menuZindex: menus.zIndexes.menu,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'spacebreaks',
      'selectedAssortmentCanvas',
      'canvasProductsFiltersSelection',
      'getFilteredProducts',
      'getHighlightedProducts',
      'canvasProducts',
      'availableProductSeries',
    ]),
    ...mapState(useClusteringStore, ['getScenarioAttributes']),
    ...mapState(useContextStore, ['getDateFormats']),
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useToolDataStore, ['customerSegments']),

    filters() {
      return this.canvasProductsFiltersSelection || [];
    },

    selectedFilterItems() {
      let filterItems = [];
      switch (this.filterValue.type) {
        case Filters.LockType:
          filterItems = [
            {
              name: this.tkey(`values.${FilterValues.Locked}`),
              id: FilterValues.Locked,
            },
            {
              name: this.tkey(`values.${FilterValues.Unlocked}`),
              id: FilterValues.Unlocked,
            },
          ];
          break;
        case FilterMappings.ProductType:
          filterItems = [
            {
              name: this.tkey(`values.${FilterValues.New}`),
              id: FilterMappings.New,
            },
            {
              name: this.tkey(`values.${FilterValues.Existing}`),
              id: FilterMappings.Existing,
            },
          ];
          break;
        case FilterMappings.CurrentSpacebreak:
        case FilterMappings.OptimisedSpacebreak:
        case FilterMappings.OriginalSpacebreak:
          filterItems = [
            ...map(this.spacebreaks, s => ({ name: s.name, id: s._id })),
            ...map(SpacebreakTypes.spacebreakTypes, st => {
              const type = upperFirst(st);
              return {
                name: this.tkey(`values.${FilterValues[type]}`),
                id: FilterMappings[type],
              };
            }),
          ];
          break;
        case Filters.PromotedDemoted:
          filterItems = [
            {
              name: this.tkey(`values.${FilterValues.PromotedOptimisation}`),
              id: FilterValues.PromotedOptimisation,
            },
            {
              name: this.tkey(`values.${FilterValues.DemotedOptimisation}`),
              id: FilterValues.DemotedOptimisation,
            },
            {
              name: this.tkey(`values.${FilterValues.PromotedReferenceCheckpoint}`),
              id: FilterValues.PromotedReferenceCheckpoint,
            },
            {
              name: this.tkey(`values.${FilterValues.DemotedReferenceCheckpoint}`),
              id: FilterValues.DemotedReferenceCheckpoint,
            },
          ];
          break;
        case Filters.customAttributes:
          filterItems = this.getValuesForSelectedAttribute(this.filterValue.id);
          break;
        case Filters.ExternalProductRanking: {
          let higherReferenceRankingName = this.tkey(
            `values.${FilterValues.HigherReferenceRanking}`
          );
          const disabled = !this.selectedAssortmentCanvas.hasBeenOptimised;
          if (disabled) {
            const mustBeOptimised = this.tkey('values.mustBeOptimised');
            higherReferenceRankingName = `${higherReferenceRankingName} (${mustBeOptimised})`;
          }
          filterItems = [
            {
              name: this.tkey(`values.${FilterValues.TopXExternalRanking}`),
              id: FilterValues.TopXExternalRanking,
            },
            {
              name: higherReferenceRankingName,
              id: FilterValues.HigherReferenceRanking,
              disabled,
            },
          ];
          break;
        }
        case Filters.CustomerSegment:
          // Creates flat list of [{name, id, customerSegmentScheme, customerSegment}]
          filterItems = this.getCustomerSegmentsFlat().map(
            ({
              customerSegmentSchemeName,
              customerSegmentScheme,
              customerSegmentName,
              customerSegment,
            }) => ({
              name: `${customerSegmentSchemeName} - ${customerSegmentName}`,
              id: `${customerSegmentScheme}-${customerSegment}`,
              customerSegmentScheme,
              customerSegment,
            })
          );
          break;
        case Filters.ProductSeries:
          filterItems = this.availableProductSeries;
          break;
        case Filters.SBChangeDate:
        case Filters.ChainIntroDate: {
          const uniqueDates = this.getUniqueDateValues(this.filterValue.type);
          // Sort the unformatted (e.g. 2025-20-01) dates and transform back to an object with both the
          // formatted and unformatted version. The unformatted version is important to avoid localisation issues when
          // using these filter values for comparisons against the values stored against products.
          const sortedDates = this.sortDates(
            uniqueDates.map(ud => ud.unformatted),
            'desc'
          ).map(unformatted => ({
            name: find(uniqueDates, { unformatted }).formatted,
            id: unformatted,
          }));

          // Return directly to avoid the default sorting behaviour which isn't suitable for dates.
          return sortedDates;
        }
        case Filters.PreviousSB: {
          const uniqueValues = this.getUniqueTextValues(this.filterValue.type);
          const sortedFilterValues = uniqueValues.sort().map(value => ({
            name: value,
            id: value,
          }));

          // Return directly to avoid the default sorting behaviour which isn't suitable for dates.
          return sortedFilterValues;
        }
        default:
          console.warn(
            `[ScenarioAssortmentsFilter] Unhandled filter type: ${this.filterValue.type}`
          );
      }

      // Apply default sorting behaviour.
      return sortBy(filterItems, item => item.name.toLowerCase());
    },

    operatorOptions() {
      // All types have equal and not equal options
      const baseOptions = [
        { name: this.tkey(FilterOperators.Equal), value: FilterOperators.Equal },
        { name: this.tkey(FilterOperators.NotEqual), value: FilterOperators.NotEqual },
      ];

      // Date filters have greater than and less than options
      if ([Filters.SBChangeDate, Filters.ChainIntroDate].includes(this.filterValue.type)) {
        return [
          ...baseOptions,
          { name: this.tkey(FilterOperators.GreaterThan), value: FilterOperators.GreaterThan },
          { name: this.tkey(FilterOperators.LessThan), value: FilterOperators.LessThan },
          {
            name: this.tkey(FilterOperators.GreaterThanOrEqual),
            value: FilterOperators.GreaterThanOrEqual,
          },
          {
            name: this.tkey(FilterOperators.LessThanOrEqual),
            value: FilterOperators.LessThanOrEqual,
          },
        ];
      }

      return baseOptions;
    },

    // TODO: Consider translations if this goes out to all clients.
    isSelectedOperatorGreaterLessThan() {
      return [
        FilterOperators.GreaterThan,
        FilterOperators.LessThan,
        FilterOperators.GreaterThanOrEqual,
        FilterOperators.LessThanOrEqual,
      ].includes(this.selectedOperator);
    },

    isOperatorInputDisabled() {
      // Disable the dropdown if no filter field is selected because
      // the set of operators available depends upon the field.
      return !this.filterValue.type;
    },

    isFilterValuesInputDisabled() {
      // Disable the dropdown if no operator is selected. This ensure allowSelectMultiple
      // is correctly applied for date-based filters.
      return !this.selectedOperator;
    },
    allowSelectMultiple() {
      // Only allow selection of multiple values on certain filters.
      return !(
        this.filterValue.type === Filters.LockType ||
        this.filterValue.type === Filters.PromotedDemoted ||
        this.filterValue.type === FilterMappings.ProductType ||
        // Selecting multiple greater/less than options doesn't make sense since they are treated as OR conditions.
        (this.filterValue.type === Filters.ChainIntroDate &&
          this.isSelectedOperatorGreaterLessThan) ||
        (this.filterValue.type === Filters.SBChangeDate && this.isSelectedOperatorGreaterLessThan)
      );
    },

    productsAndFiltersShown() {
      const res = this.mode === FILTER ? this.getFilteredProducts : this.getHighlightedProducts;
      return this.mode === FILTER
        ? `
        ${size(this.filters)} ${this.tkey('filters')},
        ${size(res.products)} ${this.tkey('productsShown')}`
        : `
        ${size(res.highlightedProducts)} ${this.tkey('highlightedProductsShown')}`;
    },

    isAddFilterDisabled() {
      // disable add filter button if no values are selected.
      // accounts for when autocomplete doesn't allow multiple selections, in that case the property is an object
      const isSingleValue = isPlainObject(this.selectedFilterValues);
      // accounts for multiple selections when property is an array
      const areMultipleValues = isArray(this.selectedFilterValues);
      const isOperatorSelected = !isEmpty(this.selectedOperator);

      if (!isOperatorSelected) {
        return true;
      }
      if (isSingleValue) {
        return false;
      }
      if (areMultipleValues) {
        return !this.selectedFilterValues.length;
      }

      return true;
    },

    labelAppliedFilters() {
      return this.mode === FILTER ? this.tkey('appliedFilters') : this.tkey('appliedHighlights');
    },

    labelNoFiltersApplied() {
      return this.mode === FILTER
        ? this.tkey('noFiltersApplied')
        : this.tkey('noHighlightsApplied');
    },

    labelFilterName() {
      return this.mode === FILTER ? this.tkey('filterName') : this.tkey('highlightName');
    },

    labelFilterValues() {
      return this.mode === FILTER ? this.tkey('filterValues') : this.tkey('highlightValues');
    },

    filterItemsSorted() {
      return sortBy(this.filterItems, item => item.name.toLowerCase());
    },

    filterValueModel: {
      get() {
        return this.filterValue.type ? this.filterValue : null;
      },
      set(val) {
        this.filterValue = val || { name: null, type: null };
      },
    },
  },

  created() {
    this.reset();
  },

  methods: {
    ...mapActions(useScenarioProductsStore, ['fetchScenarioUniqueAttributeValues']),
    ...mapActions(useAssortmentCanvasStore, [
      'setCanvasProductsFilters',
      'setCanvasProductsHighlights',
    ]),
    async reset() {
      // reset product filters
      if (this.mode === FILTER) {
        this.setCanvasProductsFilters([]);
      } else {
        this.setCanvasProductsHighlights([]);
      }
      const data = await this.fetchScenarioUniqueAttributeValues({
        scenarioId: this.selectedScenario._id,
      });
      // add type to customAttributes for filter
      this.customAttributeItemsWithType = cloneDeep(this.getScenarioAttributes).map(x => {
        x.type = Filters.customAttributes;
        return x;
      });
      this.attributesValuesMap = data;
      this.filterItems = [
        ...this.customAttributeItemsWithType,
        ...this.baseFilterItems,
        ...this.getHistoricKpiColumnsIfEnabled(),
      ];
      if (
        get(this.getClientConfig, 'features.isExternalProductRankingHighlightingEnabled', false)
      ) {
        const externalRankingFilterItem = {
          name: this.tkey(Filters.ExternalProductRanking),
          type: Filters.ExternalProductRanking,
        };
        this.filterItems = [
          ...this.customAttributeItemsWithType,
          ...this.baseFilterItems,
          externalRankingFilterItem,
          ...this.getHistoricKpiColumnsIfEnabled(),
        ];
      }
      if (get(this.getClientConfig, 'features.customerSegmentsEnabled', false)) {
        const customerSegmentFilterItem = {
          name: this.tkey(Filters.CustomerSegment),
          type: Filters.CustomerSegment,
        };
        this.filterItems = [
          ...this.customAttributeItemsWithType,
          ...this.baseFilterItems,
          customerSegmentFilterItem,
          ...this.getHistoricKpiColumnsIfEnabled(),
        ];
      }
    },
    menuArrow() {
      // allow arrow icon to close autocomplete selection menu
      const autocompleteSelect = this.$refs.autocompleteSelect;
      if (autocompleteSelect.isMenuActive) {
        this.$refs.autocompleteSelect.isMenuActive = false;
        autocompleteSelect.blur();
      } else {
        this.$refs.autocompleteSelect.isMenuActive = true;
        autocompleteSelect.focus();
      }
    },
    getValuesForSelectedAttribute(id) {
      if (id) {
        const selectableValues = this.attributesValuesMap[id].values;
        // map data to conform to {name: 'x' , id: 'x'} convention
        return map(selectableValues, x => ({ name: x, id: x }));
      }
      return [];
    },
    /**
     * Gets all the unique values for the given canvas product field, formatted as dates.
     * @param fieldName The name of the field in the canvasProducts to get unique dates for.
     * @returns { Array<{ unformatted: string, formatted: string }> } The unique date values for the given field,
     * both formatted and unformatted.
     */
    getUniqueDateValues(fieldName) {
      const datesAsText = this.getUniqueTextValues(fieldName);
      return datesAsText.map(d => ({
        unformatted: d,
        formatted: this.formatDate(d, this.getDateFormats.long),
      }));
    },
    /**
     * Gets all the unique text values for the given canvas product field.
     * @param fieldName The name of the field in the canvasProducts to get unique text values for.
     */
    getUniqueTextValues(fieldName) {
      // Get unique text values from canvas products
      const uniqueValues = new Set();
      this.canvasProducts.forEach(product => {
        const fieldValue = product[fieldName];
        if (!isEmpty(fieldValue)) {
          uniqueValues.add(fieldValue);
        }
      });

      return Array.from(uniqueValues);
    },
    addFilter() {
      this.filters.push({
        operator: this.selectedOperator,
        filterType: this.filterValue.type,
        attributeId: this.filterValue.type === Filters.customAttributes ? this.filterValue.id : '',
        filterName: this.filterValue.name,
        filterValues: isArray(this.selectedFilterValues)
          ? this.selectedFilterValues
          : [this.selectedFilterValues],
      });
      this.selectedFilterValues = [];
      this.filterValue = { name: null, type: null };
      this.selectedOperator = null;
    },
    removeFilter(index) {
      this.filters.splice(index, 1);
    },
    deleteChip(item) {
      if (isArray(this.selectedFilterValues)) {
        const index = this.selectedFilterValues.indexOf(item);
        this.selectedFilterValues.splice(index, 1);
      } else {
        this.selectedFilterValues = null;
      }
    },
    closeModal() {
      if (!this.menu) {
        if (this.mode === FILTER) {
          this.setCanvasProductsFilters(cloneDeep(this.filters));
        } else {
          this.setCanvasProductsHighlights(cloneDeep(this.filters));
        }
      }
    },
    onFilterFieldChange() {
      this.selectedOperator = null;
      this.selectedFilterValues = [];
    },
    onOperatorChange() {
      this.selectedFilterValues = [];
    },
    getHistoricKpiColumnsIfEnabled() {
      return this.historicKPIsEnabled
        ? [
            {
              name: this.tkey(Filters.PreviousSB),
              type: Filters.PreviousSB,
            },
            {
              name: this.tkey(Filters.SBChangeDate),
              type: Filters.SBChangeDate,
            },
            {
              name: this.tkey(Filters.ChainIntroDate),
              type: Filters.ChainIntroDate,
            },
          ]
        : [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.close-icon-container {
  width: 100%;
}

.btn-text {
  color: $assortment-primary-colour;
  padding-right: 0 !important;

  &::before {
    opacity: 0 !important;
  }
}

:deep(.v-btn:not(.v-btn--text):not(.v-btn--outlined):focus:before) {
  opacity: 0 !important;
}
:deep(.rtls-checkbox) {
  margin: 0 !important;

  .v-input--selection-controls__input {
    margin-right: 0 !important;
  }
}

:deep(.v-chip) {
  padding-right: 30px;
}

:deep(.v-chip__content) {
  // set max-width on the chip content so it doesn't overflow the container
  max-width: 300px;
  height: auto;
  display: inline-block !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.v-chip__close) {
  position: absolute;
  top: 3px;
  right: 12px;
  width: 24px;
}

.title-filters {
  padding-left: 5px;
}

.filter-item {
  font-size: 1.2rem;
  padding-left: 5px;
}

.filter-row {
  border-bottom: 1px solid $assortment-divider-colour;
  &:nth-child(odd) {
    background: $assortment-table-blue-bg-colour;
  }
}

.add-filter-button {
  margin-right: 3px;
}

.headers {
  border-bottom: 1px solid $assortment-divider-colour-darker;
  padding-left: 5px;
}
</style>
